// Session-end recap controller — shown when the user taps "Terminer"
// in libre or revision mode. Pulls the current stats + the
// session-start snapshot + the persisted progress history, renders a
// minimal recap, and offers two exits:
//
//   Reprendre       — no-op: close modal, session continues as-is
//                     (counters, errors, current card, snapshot intact).
//                     Also the result of Esc / backdrop click.
//   Nouvelle session — reset counters + errors + snapshot, pick a new
//                     card. Use this to "truly restart" a recap window.
//
// Défi mode has its own end modal (defiEndModal), so this controller
// is strictly for the infinite-mode case. The Terminer handler in
// bootstrap.ts routes to the right end-screen based on gameMode.

import { t } from "../i18n";
import type { AppState, CefrLevel } from "../types";
import {
  computeMasteredCount,
  computeSessionRecap,
  countCardsDueTomorrow,
} from "../domain/session-recap";
import {
  loadProgressHistory,
  type ProgressSnapshot,
} from "../state/progress";
import { starsForTier } from "../domain/level";
import { byId, hide, show } from "./dom";
import { renderErrorRows } from "./error-row";
import { escapeHtml } from "./html";

export type SessionStartSnapshot = {
  masteredCount: number;
  userLevel: CefrLevel;
};

export interface SessionEndDeps {
  getState: () => AppState;
  getSessionStartSnapshot: () => SessionStartSnapshot | null;
  /** Active deck — used to branch the progression-change line between
   *  CEFR-level (bilingual) and 5-star tier (mono). */
  getList: () => import("../config/lists").CardList;
  /** Called when the user taps "Nouvelle session" — bootstrap resets
   *  counters / errors / snapshot and picks a new card. */
  onNewSession: () => void;
  /** Called when the user taps "Voir ma progression détaillée" —
   *  closes the recap and opens the deck-stats modal so the user can
   *  see the full mastery histogram + CEFR breakdown without having
   *  to hunt for the "niv" pill in the header. */
  onOpenStats: () => void;
}

export function createSessionEnd(deps: SessionEndDeps) {
  const modal = byId("sessionEndModal");
  const titleEl = byId("sessionEndTitle");
  const statsEl = byId("sessionEndStats");
  const progressEl = byId("sessionEndProgress");
  const errorsSection = byId("sessionEndErrorsSection");
  const errorsTitleEl = byId("sessionEndErrorsTitle");
  const errorsListEl = byId("sessionEndErrorsList");
  const resumeBtn = byId<HTMLButtonElement>("sessionEndResumeBtn");
  const newBtn = byId<HTMLButtonElement>("sessionEndNewBtn");
  const statsLinkBtn = byId<HTMLButtonElement>("sessionEndStatsBtn");

  function renderProgressLines(state: AppState, start: SessionStartSnapshot): string {
    const history: ProgressSnapshot[] = loadProgressHistory(state.currentListId);
    const recap = computeSessionRecap({
      cards: state.cards,
      stats: state.stats,
      session: state.session,
      sessionStartSnapshot: start,
      currentLevel: state.userLevel,
      history,
    });
    const lines: string[] = [];
    // Forward-looking lines — "where you are" (mastery progress) and
    // "what's next" (cards due tomorrow). Keep these above the session-
    // scoped deltas so the user's eye lands on the accumulative context
    // before the per-session numbers.
    const mastered = computeMasteredCount(state.cards, state.stats);
    // ⓘ glyph + a hidden expansion note — the mastery threshold is
    // non-obvious (Leitner 5-box system), so offer a tap/hover-reachable
    // explanation rather than leaving users to wonder why it's 0.
    const masteryTip = t("session_end_mastery_tooltip") as string;
    lines.push(
      `<div class="session-end-line session-end-line-info" data-info-target="masteryInfoNote" title="${escapeHtml(masteryTip)}">` +
        `${t("session_end_mastery", mastered, state.cards.length)}` +
        ` <span class="session-end-info-glyph" aria-hidden="true">ⓘ</span>` +
        `</div>` +
        `<div class="session-end-info-note hidden" id="masteryInfoNote">${escapeHtml(masteryTip)}</div>`,
    );
    const dueTomorrow = countCardsDueTomorrow(state.cards, state.stats);
    if (dueTomorrow > 0) {
      lines.push(
        `<div class="session-end-line">${t(
          "session_end_due_tomorrow",
          dueTomorrow,
        )}</div>`,
      );
    }
    if (recap.sessionDelta > 0) {
      lines.push(
        `<div class="session-end-line">${t(
          "session_end_session_delta",
          recap.sessionDelta,
        )}</div>`,
      );
    }
    if (recap.weekDelta != null && recap.weekDelta > 0) {
      lines.push(
        `<div class="session-end-line">${t(
          "session_end_week_delta",
          recap.weekDelta,
        )}</div>`,
      );
    }
    if (recap.monthDelta != null && recap.monthDelta > 0) {
      lines.push(
        `<div class="session-end-line">${t(
          "session_end_month_delta",
          recap.monthDelta,
        )}</div>`,
      );
    }
    // Progression-change line — bilingual decks show CEFR level-up,
    // mono decks show the 5-star tier-up ("★★☆☆☆ → ★★★☆☆"). The
    // recap computes both unconditionally; picking which to render is
    // the UI's job based on deck shape.
    const isMono = deps.getList().shape === "mono";
    if (!isMono && recap.levelChange) {
      lines.push(
        `<div class="session-end-line level">${t(
          "session_end_level_change",
          recap.levelChange.from,
          recap.levelChange.to,
        )}</div>`,
      );
    }
    if (isMono && recap.tierChange) {
      const fromStars = starsForTier(recap.tierChange.from);
      const toStars = starsForTier(recap.tierChange.to);
      lines.push(
        `<div class="session-end-line level">${t(
          "session_end_tier_change",
          fromStars,
          toStars,
        )}</div>`,
      );
    }
    return lines.join("");
  }

  function open(): void {
    if (!modal) return;
    const state = deps.getState();
    const sess = state.session;
    if (titleEl) titleEl.textContent = t("session_end_title") as string;
    // Same 3-line icon recap as the défi modal (▶ total, ✓ unhinted,
    // ◐ hinted) so the two end-screens read identically. No target here
    // so the ▶ line is a plain count rather than N/target.
    if (statsEl) {
      const hintedAttempts = sess.hintedAttempts ?? 0;
      const hintedCorrect = sess.hintedCorrect ?? 0;
      const unhintedAttempts = sess.attempts - hintedAttempts;
      const unhintedCorrect = sess.correct - hintedCorrect;
      const pct = (n: number, d: number): string =>
        d > 0 ? ` · ${Math.round((n / d) * 100)}%` : "";
      const parts: string[] = [
        `<div class="defi-score">▶ ${sess.attempts}</div>`,
      ];
      if (unhintedAttempts > 0) {
        parts.push(
          `<div class="defi-stat-line">✓ ${unhintedCorrect} / ${unhintedAttempts}${pct(unhintedCorrect, unhintedAttempts)}</div>`,
        );
      }
      if (hintedAttempts > 0) {
        parts.push(
          `<div class="defi-stat-line">◐ ${hintedCorrect} / ${hintedAttempts}${pct(hintedCorrect, hintedAttempts)}</div>`,
        );
      }
      statsEl.innerHTML = parts.join("");
    }
    const start = deps.getSessionStartSnapshot();
    if (progressEl) {
      progressEl.innerHTML = start ? renderProgressLines(state, start) : "";
    }
    // "À retravailler" list — same renderer as the défi recap so both
    // end-screens stay visually identical. Only shown when the user
    // actually missed something; empty session keeps it hidden.
    if (errorsSection && errorsListEl) {
      const errs = state.session.errors;
      if (errs.length > 0) {
        if (errorsTitleEl) {
          errorsTitleEl.textContent = t("defi_errors_title") as string;
        }
        errorsListEl.innerHTML = renderErrorRows(errs);
        errorsSection.classList.remove("hidden");
      } else {
        errorsSection.classList.add("hidden");
      }
    }
    if (resumeBtn) resumeBtn.textContent = t("session_end_resume") as string;
    if (newBtn) newBtn.textContent = t("session_end_new") as string;
    if (statsLinkBtn) {
      statsLinkBtn.textContent = t("session_end_stats_link") as string;
    }
    show(modal);
  }

  /** No-op close: used by Reprendre, Esc, and backdrop click. Session
   *  state is preserved so the user picks up exactly where they
   *  paused. */
  function close(): void {
    hide(modal);
  }

  resumeBtn?.addEventListener("click", () => close());
  newBtn?.addEventListener("click", () => {
    close();
    deps.onNewSession();
  });
  statsLinkBtn?.addEventListener("click", () => {
    // Close the recap first so deckStats is the single open overlay.
    // Session state is preserved (same as "Reprendre"), so leaving
    // deckStats reopens the card at exactly where they were.
    close();
    deps.onOpenStats();
  });
  modal?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === "sessionEndModal") close();
  });
  // Delegate click on any `[data-info-target]` line to toggle the
  // sibling explanation note. Keeps the ⓘ behavior wired even though
  // the progress block is re-rendered via innerHTML on each open().
  progressEl?.addEventListener("click", (e) => {
    const line = (e.target as HTMLElement).closest<HTMLElement>(
      "[data-info-target]",
    );
    if (!line) return;
    const noteId = line.dataset.infoTarget;
    if (!noteId) return;
    byId(noteId)?.classList.toggle("hidden");
  });

  return { open, close };
}
